/**
 * @lines98/client/services/game-submit — POST /finish-game wrapper.
 *
 * Per dispatch_t-7 §2 C5 + anti-cheat.md §4 + core-daily-board.md §6:
 *   - Build canonical `FinishGameRequest` payload from store state.
 *   - POST к server with Bearer (api-client.ts auto-injects identity token).
 *   - Map server response к а discriminated `SubmitResult` for UI consumption.
 *   - Retry с simple linear backoff on transient network failures (cap 3).
 *
 * Idempotency: server derives the idempotency key from `(player_id, payload_hash)`
 * via `buildIdempotencyKey()` (per ADR-0005 + `packages/server/src/routes/finish-game.ts`).
 * Client does NOT generate а UUID — server canonicalizes payload + hashes it. This
 * gives us free retry-safety: repeated submits с the same payload return the
 * cached COMPLETED response without double-counting.
 *
 * Boundary (load-bearing): client does NOT trust `score_claimed` as the public
 * score. UI MUST render `score_verified` from the server response. Per
 * anti-cheat.md §3.2 + AC-4 + core-daily-board.md §6.1. If `score_claimed !==
 * score_verified`, server publishes verified and audits mismatch (it's NOT а
 * client-error — server-side state authoritative).
 *
 * Cross-references:
 *   - dispatch_t-7_game-developer.md §2 C5.
 *   - anti-cheat.md §4 (FinishGameRequest / FinishGameResponse shape).
 *   - core-daily-board.md §6 (leaderboard publication уровень).
 *   - ADR-0005 (idempotency-key server-derived per ADR §«Decision»).
 *   - @lines98/shared/types/game.ts (FinishGameRequest / FinishGameResponse — canon).
 *   - packages/server/src/routes/finish-game.ts (server contract).
 */

import { CURRENT_ENGINE_VERSION } from '@lines98/shared/config/balance';
import type {
  DailyOwnRow,
  FinishGameRequest,
  FinishGameResponse,
  Move,
  FinishReason,
  GameMode,
  MetaFaucetSummary,
  RejectReason,
} from '@lines98/shared/types/game';

import { apiClient, ApiClientError } from './api-client.js';

// =============================================================================
// Public types
// =============================================================================

/**
 * Inputs needed к build а daily finish-game payload. The store collects all of
 * these at endGame() time; this service just marshals them.
 */
export interface DailySubmitInput {
  readonly context: { kind: 'daily'; dateOfDay: string };
  readonly seed: number;
  readonly moves: Move[];
  readonly scoreClaimed: number;
  readonly linesClaimed: number;
  readonly finishReason: FinishReason;
  /** Mode is locked to 'predict' for daily (core-daily-board.md §3.4) but accept here для symmetry. */
  readonly mode: GameMode;
}

/**
 * Inputs to submit a non-daily server-scored game (extra-modes, ADR-0013). The
 * `sessionId` + `seed` come from `POST /game/start`; the server cross-checks the
 * seed against the session (closes the trust-client-seed hole). `mode` is the
 * player-chosen one (display metadata; the server takes it authoritatively from the
 * session). No rollover for non-daily (§2.2; finish_reason ∈ board_full/player_finish).
 */
export interface AllTimeSubmitInput {
  readonly context: { kind: 'all_time'; sessionId: string };
  readonly seed: number;
  readonly moves: Move[];
  readonly scoreClaimed: number;
  readonly linesClaimed: number;
  readonly finishReason: FinishReason;
  readonly mode: GameMode;
}

/**
 * Discriminated union of server outcomes, mapped к UI-relevant cases. UI consumers
 * branch on `.status`.
 *
 * **Spec invariant (anti-cheat.md §3.5 + core-daily-board.md AC-13 + §7.1):** a
 * daily attempt is "consumed" only when server's atomic `daily_attempts` pre-claim
 * succeeds (`packages/server/src/routes/finish-game.ts:297`). Rejects that fire
 * BEFORE the pre-claim (`rate_limited` at the plugin level + `engine_mismatch` at
 * routes/finish-game.ts:211) do NOT consume the attempt — client MUST NOT cache
 * `played` for these statuses (would silently leak а day to the player on
 * recoverable failures). Statuses below are split к make this distinction
 * type-level и trivially enforceable.
 *
 * - `verified` — game accepted, score_verified is canonical public score.
 * - `duplicate_attempt` — player already played today (server `daily_attempts`
 *   atomic conflict). Cache flips к done-state per core-daily-board.md §2.2.
 * - `rejected` — server validation failed AFTER pre-claim (invalid_move /
 *   invalid_seed / unknown). Daily attempt WAS consumed по policy Q5
 *   (anti-cheat.md §3.5). UI must save played-cache.
 * - `rate_limited` — server throttled the request at the plugin level, BEFORE
 *   pre-claim (rate-limit.ts:101-162). Daily attempt NOT consumed
 *   (anti-cheat.md §3.5 «rate_limited исключение»). UI must offer retry, NOT
 *   cache played.
 * - `engine_mismatch` — `client_engine_version !== server's CURRENT_ENGINE_VERSION`.
 *   Reject fires BEFORE pre-claim (finish-game.ts:211 — line 211 < line 297).
 *   Daily attempt NOT consumed. Spec §7.2 says policy is «тратит» but server
 *   implementation does not pre-claim before this check; client mirrors the
 *   actual server contract. T-11 multi-version registry will smooth this.
 * - `network` — fetch reject or 5xx; caller can retry. Daily attempt NOT
 *   consumed (server didn't pre-claim).
 */
/**
 * Subset of `RejectReason` (anti-cheat.md §4.2 enum) that surfaces as the
 * `rejected` branch — i.e., rejects that fire AFTER server's atomic
 * `daily_attempts` pre-claim succeeds, so the daily attempt IS consumed and
 * the client must cache `played`. `duplicate_attempt`, `engine_mismatch`, и
 * `rate_limited` get their own status values for cleaner UI branching и
 * correct cache discipline.
 */
export type RejectedReason = Exclude<
  RejectReason,
  'duplicate_attempt' | 'engine_mismatch' | 'rate_limited'
>;

export type SubmitResult =
  | {
      readonly status: 'verified';
      readonly gameId: string;
      readonly scoreVerified: number;
      readonly linesVerified: number;
      readonly replayUrl: string;
      readonly rank?: number;
      /**
       * The player's OWN daily row (rank + own short_id), server-computed at finish
       * time (brief-44 §B P1). Present on a verified DAILY submit; absent for all-time.
       * Lets the leaderboard paint the just-played result with no round-trip (P2 —
       * optimistic own row). `rank` here == the top-level `rank` field. Server-truth
       * (`score_verified` etc. are verified) — the client never forges it.
       */
      readonly me?: DailyOwnRow;
      /**
       * Meta «Ювелир» faucet loot for THIS game (ADR-0015 Срез 1a; server-truth,
       * NOT a client claim — AC-12). Drives the reward block on the result-screen
       * (Срез 1b, meta-jeweler-surfaces.md §4.1). Absent if the server omitted it
       * (meta disabled / idempotent no-op retry). The reward block reads it from
       * the store's `dailySubmitResult`; it NEVER feeds back into score (I-1).
       */
      readonly meta?: MetaFaucetSummary;
    }
  | {
      readonly status: 'duplicate_attempt';
    }
  | {
      readonly status: 'rejected';
      readonly reason: RejectedReason;
      readonly atMove?: number;
    }
  | {
      readonly status: 'engine_mismatch';
    }
  | {
      readonly status: 'rate_limited';
    }
  | {
      readonly status: 'network';
      readonly attempt: number;
    };

// =============================================================================
// Retry policy
// =============================================================================

const MAX_ATTEMPTS = 3 as const;
/** Linear backoff (ms): 0, 500, 1500. Total worst-case wait ≈ 2s. */
const BACKOFF_MS = [0, 500, 1500] as const;

// =============================================================================
// Payload builder (pure)
// =============================================================================

/**
 * Construct the canonical `FinishGameRequest` from а daily submit input. Pure;
 * exported для testability.
 */
export function buildDailyPayload(input: DailySubmitInput): FinishGameRequest {
  return {
    context: { kind: 'daily', date_of_day: input.context.dateOfDay },
    seed_assumed: input.seed >>> 0,
    mode: input.mode,
    moves: input.moves,
    score_claimed: input.scoreClaimed,
    lines_claimed: input.linesClaimed,
    finish_reason: input.finishReason,
    client_engine_version: CURRENT_ENGINE_VERSION,
  };
}

/**
 * Construct the canonical `FinishGameRequest` for a non-daily server-scored game
 * (extra-modes, ADR-0013). `context.kind='all_time'` carries the server `session_id`;
 * `seed_assumed` is the server-issued seed (the server cross-checks it). Pure;
 * exported для testability.
 */
export function buildAllTimePayload(input: AllTimeSubmitInput): FinishGameRequest {
  return {
    context: { kind: 'all_time', session_id: input.context.sessionId },
    seed_assumed: input.seed >>> 0,
    mode: input.mode,
    moves: input.moves,
    score_claimed: input.scoreClaimed,
    lines_claimed: input.linesClaimed,
    finish_reason: input.finishReason,
    client_engine_version: CURRENT_ENGINE_VERSION,
  };
}

// =============================================================================
// Public API — submit
// =============================================================================

/**
 * Submit а finished daily-board game к server. Retries up к MAX_ATTEMPTS times
 * on transient network failures.
 *
 * Per dispatch_t-7 §2 C5 + anti-cheat.md §4.2:
 *   - 200 ok:true → `{ status: 'verified', score_verified, ... }`.
 *   - reason `duplicate_attempt` → AC-2 path (`{ status: 'duplicate_attempt' }`).
 *   - reason `engine_mismatch` → `{ status: 'engine_mismatch' }` (NOT consumed —
 *     server rejects BEFORE atomic pre-claim).
 *   - reason `rate_limited` → `{ status: 'rate_limited' }` (NOT consumed —
 *     plugin-level reject BEFORE route handler runs, per anti-cheat.md §3.5).
 *   - other reasons (invalid_move / invalid_seed / unknown) →
 *     `{ status: 'rejected', reason, atMove? }` (consumed — server rejects
 *     AFTER atomic pre-claim per Q5).
 *   - network throw / 5xx → retry с backoff; final failure → `{ status: 'network' }`.
 *
 * @param input — daily submit input (already collected at endGame time).
 * @param options.sleep — inject-able sleep для tests (default `setTimeout` Promise).
 */
export async function submitFinishedDailyGame(
  input: DailySubmitInput,
  options: { sleep?: (ms: number) => Promise<void> } = {},
): Promise<SubmitResult> {
  return postFinishGameWithRetry(buildDailyPayload(input), options);
}

/**
 * Submit a finished non-daily server-scored game (extra-modes, ADR-0013). Same
 * retry/mapping semantics as the daily submit — only the payload context differs
 * (`{ kind:'all_time', session_id }`). A successful submit publishes
 * `score_verified` and (if a personal best) updates the all-time leaderboard
 * server-side; there is NO `duplicate_attempt` (no one-per-day limit, AC-3).
 *
 * @param input — all-time submit input (collected at endGame time).
 * @param options.sleep — inject-able sleep для tests.
 */
export async function submitFinishedAllTimeGame(
  input: AllTimeSubmitInput,
  options: { sleep?: (ms: number) => Promise<void> } = {},
): Promise<SubmitResult> {
  return postFinishGameWithRetry(buildAllTimePayload(input), options);
}

/**
 * Shared `POST /finish-game` submit loop with linear backoff on transient network
 * failures (cap MAX_ATTEMPTS). Both the daily and all-time submitters call this —
 * the retry + response-mapping rule lives in exactly one place (no drift between
 * the two surfaces). Mapping per anti-cheat.md §4.2 (see {@link mapResponse}).
 */
async function postFinishGameWithRetry(
  payload: FinishGameRequest,
  options: { sleep?: (ms: number) => Promise<void> } = {},
): Promise<SubmitResult> {
  const sleep =
    options.sleep ??
    ((ms: number): Promise<void> =>
      new Promise((resolve) => {
        if (ms <= 0) {
          resolve();
          return;
        }
        (globalThis as { setTimeout: (cb: () => void, ms: number) => unknown }).setTimeout(
          () => resolve(),
          ms,
        );
      }));

  let lastAttempt = 0;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    lastAttempt = attempt;
    const backoff = BACKOFF_MS[attempt - 1] ?? BACKOFF_MS[BACKOFF_MS.length - 1]!;
    if (backoff > 0) await sleep(backoff);

    let response;
    try {
      response = await apiClient.post<FinishGameResponse>('/finish-game', payload);
    } catch (err) {
      if (err instanceof ApiClientError && err.kind === 'network') {
        // Transient — retry до cap.
        if (attempt < MAX_ATTEMPTS) continue;
        return { status: 'network', attempt };
      }
      // Unknown thrown — treat as network для conservative retry behavior.
      if (attempt < MAX_ATTEMPTS) continue;
      return { status: 'network', attempt };
    }

    // 5xx — transient; retry.
    if (!response.ok && response.status >= 500) {
      if (attempt < MAX_ATTEMPTS) continue;
      return { status: 'network', attempt };
    }

    // From here, response.body should match FinishGameResponse shape (success OR
    // typed failure). 2xx + body.ok:true is success path; 2xx или 4xx с
    // body.ok:false is а typed rejection.
    return mapResponse(response.body);
  }

  // Unreachable — loop guarantees return — but TS satisfies needs it.
  return { status: 'network', attempt: lastAttempt };
}

/**
 * Map а raw `FinishGameResponse`-shaped body к а `SubmitResult`. Exported для
 * unit tests (and для defensive shape-check сборки).
 */
export function mapResponse(body: unknown): SubmitResult {
  if (!body || typeof body !== 'object') {
    return { status: 'rejected', reason: 'unknown' };
  }
  const v = body as Record<string, unknown>;

  if (v['ok'] === true) {
    return {
      status: 'verified',
      gameId: String(v['game_id'] ?? ''),
      scoreVerified: Number(v['score_verified'] ?? 0),
      linesVerified: Number(v['lines_verified'] ?? 0),
      replayUrl: String(v['replay_url'] ?? ''),
      ...(typeof v['rank'] === 'number' ? { rank: v['rank'] } : {}),
      // Own daily row (brief-44 §B P1) — verbatim server-truth (validated defensively
      // so a malformed / absent field degrades to «no optimistic row», never NaN).
      ...(isDailyOwnRow(v['me']) ? { me: v['me'] } : {}),
      // Meta faucet summary (ADR-0015 Срез 1a) — passed through verbatim as
      // server-truth (no client recompute, AC-12). Validated defensively so a
      // malformed / absent field never crashes the reward block.
      ...(isMetaSummary(v['meta']) ? { meta: v['meta'] } : {}),
    };
  }

  if (v['ok'] === false) {
    const reason = v['reason'];
    if (reason === 'duplicate_attempt') {
      return { status: 'duplicate_attempt' };
    }
    if (reason === 'engine_mismatch') {
      // Rejected BEFORE server pre-claim (finish-game.ts:211 fires before line
      // 297) — daily attempt NOT consumed. See SubmitResult union doc.
      return { status: 'engine_mismatch' };
    }
    if (reason === 'rate_limited') {
      // Rejected at plugin level BEFORE route handler runs (rate-limit.ts) —
      // daily attempt NOT consumed (anti-cheat.md §3.5 «rate_limited
      // исключение»). Caller must offer retry, NOT cache played.
      return { status: 'rate_limited' };
    }
    if (
      reason === 'invalid_seed' ||
      reason === 'invalid_move' ||
      reason === 'unknown'
    ) {
      // Rejected AFTER server pre-claim — daily attempt IS consumed (Q5).
      const result: SubmitResult = {
        status: 'rejected',
        reason: reason,
        ...(typeof v['at_move'] === 'number' ? { atMove: v['at_move'] } : {}),
      };
      return result;
    }
  }

  // Unrecognized shape — defensive default.
  return { status: 'rejected', reason: 'unknown' };
}

/**
 * Defensive shape-check for the optional `meta` faucet summary (ADR-0015 Срез 1a;
 * камни-ресурс T1). The reward block only renders when the core numeric fields are
 * present and finite + `gem_pool_delta` is an array — a partial / future-extended
 * payload safely degrades to «no reward block» rather than rendering NaN. Exported
 * for unit tests.
 */
/**
 * Defensive shape-check for the optional `me` daily row (brief-44 §B P1). The
 * optimistic-own-row path (P2) only records it when rank + short_id (the load-bearing
 * fields) are present and well-typed — a partial / future-extended payload safely
 * degrades to «no optimistic row» (client read-fetches its row) rather than rendering
 * NaN / undefined. Exported for unit tests.
 */
export function isDailyOwnRow(value: unknown): value is DailyOwnRow {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    Number.isFinite(v['rank']) &&
    typeof v['short_id'] === 'string' &&
    v['short_id'].length > 0 &&
    Number.isFinite(v['score_verified']) &&
    Number.isFinite(v['lines_verified']) &&
    Number.isFinite(v['moves_count'])
  );
}

export function isMetaSummary(value: unknown): value is MetaFaucetSummary {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    Number.isFinite(v['gold_earned']) &&
    Number.isFinite(v['shards_earned']) &&
    Number.isFinite(v['gems_dropped']) &&
    Array.isArray(v['gem_pool_delta'])
  );
}
